import { readShipmentCharacteristics, type ShipmentCharacteristicsData } from './shipmentInformation'
import { formatAddressForDisplay } from './addressDisplay'

export type RoutePoint = {
  address?: string
  coordinates?: [number, number]
}

export type ShipmentRouteData = ShipmentCharacteristicsData & {
  route?: string
  points?: RoutePoint[]
  order_number?: string
  loading_date?: string
  unloading_date?: string
  planned_delivery_time?: string
  vehicle_type?: string
  driver_name?: string
  company?: string
  contact?: string
  driver_phone?: string
  vehicle_number?: string
  trailer_number?: string
  manager_name?: string
  manager_company?: string
  manager_phone?: string
  manager_email?: string
}

/**
 * Never throws. Handles string | object | null | garbage.
 * Performs light shape normalization: non-array points become undefined,
 * non-string scalar fields are dropped. This is NOT a full schema validator.
 * Coordinate range validation (lat/lng bounds) is the responsibility of
 * isUsableMapCoordinate in routeUtils.ts.
 */
export function parseRouteData(json: unknown): ShipmentRouteData | null {
  if (!json) return null
  try {
    const raw = typeof json === 'string' ? JSON.parse(json) : json
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
    const r = raw as Record<string, unknown>
    return {
      ...readShipmentCharacteristics(r),
      route: typeof r.route === 'string' ? r.route : undefined,
      points: Array.isArray(r.points)
        ? (r.points as unknown[]).reduce<RoutePoint[]>((acc, item) => {
            if (typeof item !== 'object' || item === null || Array.isArray(item)) return acc
            const p = item as Record<string, unknown>
            acc.push({
              address: typeof p.address === 'string' ? p.address : undefined,
              coordinates: Array.isArray(p.coordinates) && p.coordinates.length >= 2
                ? p.coordinates as [number, number]
                : undefined,
            })
            return acc
          }, [])
        : undefined,
      order_number: typeof r.order_number === 'string' ? r.order_number : undefined,
      loading_date: typeof r.loading_date === 'string' ? r.loading_date : undefined,
      unloading_date: typeof r.unloading_date === 'string' ? r.unloading_date : undefined,
      planned_delivery_time: typeof r.planned_delivery_time === 'string' ? r.planned_delivery_time : undefined,
      vehicle_type: typeof r.vehicle_type === 'string' ? r.vehicle_type : undefined,
      driver_name: typeof r.driver_name === 'string' ? r.driver_name : undefined,
      company: typeof r.company === 'string' ? r.company : undefined,
      contact: typeof r.contact === 'string' ? r.contact : undefined,
      driver_phone: typeof r.driver_phone === 'string' ? r.driver_phone : undefined,
      vehicle_number: typeof r.vehicle_number === 'string' ? r.vehicle_number : undefined,
      trailer_number: typeof r.trailer_number === 'string' ? r.trailer_number : undefined,
      manager_name: typeof r.manager_name === 'string' ? r.manager_name : undefined,
      manager_company: typeof r.manager_company === 'string' ? r.manager_company : undefined,
      manager_phone: typeof r.manager_phone === 'string' ? r.manager_phone : undefined,
      manager_email: typeof r.manager_email === 'string' ? r.manager_email : undefined,
    }
  } catch {
    return null
  }
}

export function getRouteText(json: unknown): string {
  return parseRouteData(json)?.route ?? ''
}

export function getRoutePoints(json: unknown): RoutePoint[] {
  return parseRouteData(json)?.points ?? []
}

/** Consistent full address labels for detail views; saved route data stays intact. */
export function getRouteAddresses(json: unknown): string[] {
  const data = parseRouteData(json)
  const addresses = data?.points?.map((point) => point.address?.trim()).filter((address): address is string => Boolean(address)) ?? []
  if (addresses.length >= 2) return addresses.map(formatAddressForDisplay)
  const route = data?.route?.trim()
  return (route ? route.split(/\s*→\s*/).map((address) => address.trim()).filter(Boolean) : addresses).map(formatAddressForDisplay)
}

export function getFullRouteLabel(json: unknown): string {
  return getRouteAddresses(json).join(' → ')
}
